import React, { useState } from 'react';
import type { GoogleVeoModel, VideoResolution, VideoAiProvider } from './videoStudioTypes';
import { videoStudioApi } from './videoStudioApi';

interface VeoGenerationCardProps {
  campaignId: string;
  creativeId: string;
  targetDurationSeconds?: number;
  sceneCount?: number;
  onBeforeGenerate?: () => Promise<any>;
  onSuccess: (videoUrl: string) => void;
  onError?: (error: string | null) => void;
}

export const VeoGenerationCard: React.FC<VeoGenerationCardProps> = ({
  campaignId,
  creativeId,
  targetDurationSeconds = 15,
  sceneCount = 3,
  onBeforeGenerate,
  onSuccess,
  onError,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [provider, setProvider] = useState<VideoAiProvider>('google');
  const [modelVersion, setModelVersion] = useState<GoogleVeoModel>('veo-3.1-fast');
  const [resolution, setResolution] = useState<VideoResolution>('720p');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleApiKeyChange = (val: string) => {
    setApiKey(val);
    const trimmed = val.trim();
    if (trimmed.startsWith('AIza') || trimmed.startsWith('AQ.')) {
      setProvider('google');
    } else if (trimmed.startsWith('r8_')) {
      setProvider('replicate');
    } else if (trimmed.startsWith('fal_') || trimmed.includes(':')) {
      setProvider('fal');
    }
  };

  const handleGenerate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setIsGenerating(true);
    setLocalError(null);
    if (onError) onError(null);

    const modelName = modelVersion === 'veo-3.1-fast' ? 'Veo 3.1 Fast' : 'Veo 3.1';
    setStatusMsg(`Salvando roteiro e durações recalculadas antes de renderizar...`);

    try {
      let effectiveDuration = targetDurationSeconds;
      if (onBeforeGenerate) {
        const savedProject = await onBeforeGenerate();
        if (savedProject?.target_duration_seconds) {
          effectiveDuration = savedProject.target_duration_seconds;
        }
      }

      setStatusMsg(`Renderizando com Google ${modelName} (${resolution.toUpperCase()} • ${effectiveDuration}s)... Aguarde 1 a 2 minutos.`);

      const res = await videoStudioApi.generateWithVeo(campaignId, creativeId, {
        api_key: apiKey.trim() || undefined,
        provider,
        model_version: modelVersion,
        resolution,
        duration_seconds: effectiveDuration,
      });

      if (res.ok && res.video_url) {
        setStatusMsg(`✓ Vídeo gerado com sucesso pelo Google ${modelName} (${resolution.toUpperCase()})!`);
        onSuccess(res.video_url);
        setShowConfig(false);
        setTimeout(() => setStatusMsg(null), 5000);
      } else if (res.error === 'key_required') {
        setShowConfig(true);
        const msg = res.message || 'Chave de API do Google Cloud / AI Studio necessária.';
        setLocalError(msg);
        if (onError) onError(msg);
        setStatusMsg(null);
      } else {
        setShowConfig(true);
        const msg = res.message || 'Falha ao renderizar vídeo com Google Veo 3.1.';
        setLocalError(msg);
        if (onError) onError(msg);
        setStatusMsg(null);
      }
    } catch (err: any) {
      setShowConfig(true);
      const msg = err.message || 'Erro durante a renderização no Google Veo 3.1';
      setLocalError(msg);
      if (onError) onError(msg);
      setStatusMsg(null);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(124, 58, 237, 0.12))',
      border: '1px solid rgba(99, 102, 241, 0.35)',
      borderRadius: '0.85rem',
      padding: '1.25rem',
      marginBottom: '1.5rem',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
            <span style={{ fontSize: '1.3rem' }}>🚀</span>
            <strong style={{ fontSize: '1.05rem' }}>Google Cloud Veo 3.1 & Veo Fast</strong>
            <span className="vstudio-badge vstudio-badge-primary">
              {resolution === '720p' ? '⚡ 720p Econômico' : resolution === '1080p' ? '🌟 1080p Full HD' : '💎 4K Ultra'}
            </span>
            <span className="vstudio-badge vstudio-badge-success">
              🎬 {sceneCount} cenas ({targetDurationSeconds}s)
            </span>
          </div>
          <p style={{ margin: 0, fontSize: '0.825rem', color: 'var(--text-secondary, #64748b)', maxWidth: '540px', lineHeight: '1.4' }}>
            Gere o vídeo oficial automaticamente via Google Cloud com Veo 3.1 adaptado dinamicamente para as {sceneCount} cenas definidas no estúdio ({targetDurationSeconds}s).
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          {/* Seletor Veo 3.1 vs Veo 3.1 Fast */}
          <div style={{
            display: 'inline-flex',
            background: 'rgba(255, 255, 255, 0.7)',
            borderRadius: '0.5rem',
            padding: '2px',
            border: '1px solid rgba(99, 102, 241, 0.3)',
          }}>
            <button
              type="button"
              onClick={() => setModelVersion('veo-3.1-fast')}
              style={{
                background: modelVersion === 'veo-3.1-fast' ? '#2563eb' : 'transparent',
                color: modelVersion === 'veo-3.1-fast' ? '#fff' : '#475569',
                border: 'none',
                borderRadius: '0.35rem',
                padding: '0.35rem 0.65rem',
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
              title="Veo 3.1 Fast: Renderização ultrarrápida com menor consumo de créditos"
            >
              ⚡ Veo 3.1 Fast
            </button>
            <button
              type="button"
              onClick={() => setModelVersion('veo-3.1')}
              style={{
                background: modelVersion === 'veo-3.1' ? '#2563eb' : 'transparent',
                color: modelVersion === 'veo-3.1' ? '#fff' : '#475569',
                border: 'none',
                borderRadius: '0.35rem',
                padding: '0.35rem 0.65rem',
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
              title="Veo 3.1: Máxima fidelidade cinematográfica e detalhes visuais"
            >
              ✨ Veo 3.1
            </button>
          </div>

          {/* Seletor de Resolução */}
          <div style={{
            display: 'inline-flex',
            background: 'rgba(255, 255, 255, 0.7)',
            borderRadius: '0.5rem',
            padding: '2px',
            border: '1px solid rgba(16, 185, 129, 0.4)',
          }}>
            <button
              type="button"
              onClick={() => setResolution('720p')}
              style={{
                background: resolution === '720p' ? '#10b981' : 'transparent',
                color: resolution === '720p' ? '#fff' : '#475569',
                border: 'none',
                borderRadius: '0.35rem',
                padding: '0.35rem 0.65rem',
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              ⚡ 720p
            </button>
            <button
              type="button"
              onClick={() => setResolution('1080p')}
              style={{
                background: resolution === '1080p' ? '#10b981' : 'transparent',
                color: resolution === '1080p' ? '#fff' : '#475569',
                border: 'none',
                borderRadius: '0.35rem',
                padding: '0.35rem 0.65rem',
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              🌟 1080p
            </button>
            <button
              type="button"
              onClick={() => setResolution('4k')}
              style={{
                background: resolution === '4k' ? '#10b981' : 'transparent',
                color: resolution === '4k' ? '#fff' : '#475569',
                border: 'none',
                borderRadius: '0.35rem',
                padding: '0.35rem 0.65rem',
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              💎 4K
            </button>
          </div>

          <button
            type="button"
            className="vstudio-btn"
            style={{
              background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
              color: '#ffffff',
              fontWeight: 700,
              padding: '0.65rem 1.25rem',
              fontSize: '0.875rem',
              boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
              border: 'none',
              cursor: 'pointer',
            }}
            onClick={() => handleGenerate()}
            disabled={isGenerating}
          >
            {isGenerating
              ? `⏳ Renderizando (${resolution.toUpperCase()})...`
              : `🚀 Gerar com ${modelVersion === 'veo-3.1-fast' ? 'Veo 3.1 Fast' : 'Veo 3.1'} (${resolution.toUpperCase()})`}
          </button>

          <button
            type="button"
            className="vstudio-btn vstudio-btn-outline"
            style={{ fontSize: '0.8rem', padding: '0.65rem 0.75rem' }}
            onClick={() => setShowConfig(!showConfig)}
            title="Configurar credenciais e provedor do Google Veo 3.1"
          >
            ⚙️ Provedor
          </button>
        </div>
      </div>

      {statusMsg && (
        <div style={{ marginTop: '0.85rem', padding: '0.6rem 0.85rem', borderRadius: '0.5rem', background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', color: '#16a34a', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>{isGenerating ? '⏳' : '✓'}</span>
          <span>{statusMsg}</span>
        </div>
      )}

      {localError && (
        <div style={{ marginTop: '0.75rem', padding: '0.65rem 0.85rem', borderRadius: '0.5rem', background: '#fee2e2', border: '1px solid #f87171', color: '#991b1b', fontSize: '0.85rem' }}>
          <div>{localError}</div>
          {(localError.includes('Replicate') || localError.includes('descontinuou') || localError.includes('Google Flow')) && (
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '0.35rem', padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                onClick={() => { setProvider('replicate'); setShowConfig(true); }}
              >
                🔄 Usar Provedor Replicate (Veo 3.1)
              </button>
              <button
                type="button"
                style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '0.35rem', padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                onClick={() => { setProvider('fal'); setShowConfig(true); }}
              >
                🔄 Usar Provedor Fal.ai (Veo 3.1)
              </button>
              <button
                type="button"
                style={{ background: '#475569', color: '#fff', border: 'none', borderRadius: '0.35rem', padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                onClick={() => {
                  const flowSection = document.querySelector('.vstudio-card');
                  if (flowSection) flowSection.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                📦 Alternativa Manual: Google Flow
              </button>
            </div>
          )}
        </div>
      )}

      {showConfig && (
        <form onSubmit={handleGenerate} style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(99, 102, 241, 0.2)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr auto', gap: '0.75rem', alignItems: 'flex-end' }}>
            <div>
              <label className="vstudio-form-label">Provedor de Vídeo</label>
              <select
                className="vstudio-select"
                value={provider}
                onChange={(e) => setProvider(e.target.value as VideoAiProvider)}
              >
                <option value="google">Google Cloud (Gemini Omni 1.1 & Veo 3.1)</option>
                <option value="replicate">Replicate (Veo 3.1 & Kling 3.0)</option>
                <option value="fal">Fal.ai (Veo 3.1 & Kling)</option>
              </select>
            </div>
            <div>
              <label className="vstudio-form-label">Chave de API (Opcional - usa do sistema se vazio)</label>
              <input
                type="password"
                className="vstudio-input"
                value={apiKey}
                onChange={(e) => handleApiKeyChange(e.target.value)}
                placeholder="Deixe em branco para usar chave já salva no CRM ou cole nova"
              />
            </div>
            <button
              type="submit"
              className="vstudio-btn vstudio-btn-primary"
              disabled={isGenerating}
            >
              Salvar & Renderizar
            </button>
          </div>
          <p style={{ margin: '0.35rem 0 0 0', fontSize: '0.75rem', color: 'var(--text-secondary, #64748b)' }}>
            * O CRM utiliza nativamente os modelos oficiais Veo 3.1 e Veo 3.1 Fast, com fallback multi-provedor transparente em caso de indisponibilidade da nuvem.
          </p>
        </form>
      )}
    </div>
  );
};
